import { lazy, useEffect, useState, type ReactNode } from 'react';

import { TPV_NO_DEVICE_BOOT, tpvBootDoor, type TpvBootDoorInput, type TpvDeviceBoot } from './door-decision';
import { TpvOtherRestaurantBand } from './tablet-band';

/**
 * docs/decisions/2026-10-28-tpv-sin-conexion.md · the offline door is drawn from the TPV's chunk, the one the service
 * worker keeps on every TPV (the only devices whose door can open); the entry holds only the decision.
 */
const TpvOfflineStage = lazy(() => import('../runtime-screen').then((module) => ({ default: module.TpvOfflineStage })));

/**
 * What the shell reads of this tablet at boot, beside the identity restore: the offline door of the session this tab
 * holds and the entries waiting to go up (`readTpvDeviceBoot`), and the probes of the Service's public clock. The TPV's
 * offline data belongs to the signed-in session (docs/decisions/2026-10-31-tpv-offline-without-a-caja-tablet.md):
 * `retain` keeps only that person's (`retainTpvDeviceFor`). The browser composition passes one; without it (specs) the
 * shell reads no device.
 */
export type TpvBootDevicePort = Readonly<{
  read: () => Promise<TpvDeviceBoot>;
  /** Removes every other person's offline data from the device (everyone's for null); the unsent entries stay. */
  retain: (principalId: string | null) => Promise<void>;
  /** At boot: false only when the Service's public clock failed twice, 5 s apart. */
  answersAtBoot: () => Promise<boolean>;
  /** Behind the door: true when the Service's public clock answered once. */
  probe: () => Promise<boolean>;
}>;

export const browserTpvBootDevice = (runtime: Readonly<{
  localSessionDigest: () => string | null;
  readPublicClock: () => Promise<Readonly<{ server_time: string }>>;
}>): TpvBootDevicePort => ({
  read: () => import('./door').then((module) => module.readTpvDeviceBoot({ sessionDigest: runtime.localSessionDigest() })),
  retain: (principalId) => import('./door').then((module) => module.retainTpvDeviceFor(principalId)),
  answersAtBoot: () => import('./door').then((module) => module.tpvServiceAnswersAtBoot(runtime.readPublicClock)),
  probe: () => import('./door').then((module) => module.tpvServiceAnswers(runtime.readPublicClock)),
});

const unsentSentence = (count: number): string =>
  `Hay ${count} ${count === 1 ? 'operación' : 'operaciones'} sin subir en esta tablet. Entra con tu usuario del TPV para subirlas.`;

export interface TpvBootDoor {
  /** Neither door is drawn yet. */
  readonly pending: boolean;
  /** The TPV alone, over the session's kit: the shell draws this and nothing else. */
  readonly stage: ReactNode;
  /** The line of the login door. */
  readonly loginBand: string | null;
  /** The line above the Panel. */
  readonly band: ReactNode;
}

/**
 * The offline door, the login door's band and another restaurant's entries. The device is read at boot and again
 * whenever the identity stage or the restaurant changes; the public clock is asked once per stage that could open the
 * door (a session this tab holds but cannot confirm), and «Entrar» after the return counts as an answer. Before each
 * read the device keeps only the signed-in person's offline data
 * (docs/decisions/2026-10-31-tpv-offline-without-a-caja-tablet.md): signed out or expired, nobody's; ready, that
 * person's; any other stage leaves it as it is.
 */
export function useTpvBootDoor({ device, identity, owner, restaurantId, online, onReturn }: Readonly<{
  device: TpvBootDevicePort | null;
  identity: TpvBootDoorInput['identity'];
  /** Whose offline data stays on the device: a person, nobody (null), or leave it (undefined). */
  owner: string | null | undefined;
  restaurantId: string | null;
  online: boolean;
  /** «Entrar» once the connection is back: the identity flow continues. */
  onReturn: () => void;
}>): TpvBootDoor {
  const [boot, setBoot] = useState<TpvDeviceBoot | null>(null);
  const [doorClock, setDoorClock] = useState<Readonly<{ identity: string; clock: TpvBootDoorInput['clock'] }>>({ identity: '', clock: 'unknown' });
  useEffect(() => {
    if (device === null) return undefined;
    let live = true;
    const retained = owner === undefined ? Promise.resolve() : device.retain(owner).catch(() => undefined);
    if (identity !== 'other') {
      void retained.then(() => device.read()).catch(() => TPV_NO_DEVICE_BOOT).then((read) => { if (live) setBoot(read); });
    }
    return () => { live = false; };
  }, [owner, identity, restaurantId, device]);
  const doorPossible = device !== null && identity === 'no-session';
  const clock = doorClock.identity === identity ? doorClock.clock : 'unknown';
  const doorAvailable = boot?.door != null;
  useEffect(() => {
    if (device === null || !doorPossible || !doorAvailable || clock !== 'unknown') return undefined;
    let live = true;
    void device.answersAtBoot().catch(() => false).then((answered) => {
      if (live) setDoorClock({ identity, clock: answered ? 'answered' : 'failed' });
    });
    return () => { live = false; };
  }, [clock, doorAvailable, identity, doorPossible, device]);
  const door = tpvBootDoor({ device: boot ?? TPV_NO_DEVICE_BOOT, identity, sessionRestaurantId: restaurantId, navigatorOnline: online, clock });
  // Until the device is read (the login door's band, the offline door), and while the clock is asked with the door
  // available, neither door is drawn yet.
  const devicePending = device !== null && (identity === 'signed_out' || doorPossible) && boot === null;
  const returnFromDoor = () => {
    setDoorClock({ identity, clock: 'answered' });
    onReturn();
  };
  return {
    pending: devicePending || (doorPossible && doorAvailable && online && clock === 'unknown'),
    stage: door.kind === 'offline-door' && device !== null ? <TpvOfflineStage door={door.door} onReturn={returnFromDoor} probe={device.probe} /> : null,
    loginBand: door.kind === 'login-band' ? unsentSentence(door.pendingCount) : null,
    band: door.kind === 'other-restaurant' ? <TpvOtherRestaurantBand /> : null,
  };
}
